import { getOrderPlatforms } from 'api/orderPlatforms'
import { useEffect, useState, useCallback } from 'react'
import { debounce } from 'lodash'

const useLocationOrderPlatforms = () => {
  const defaultSelectedPlatform = 'All Platforms'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [platforms, setPlatforms] = useState<{ id: string; name: string }[]>([
    { id: 'all-platforms', name: defaultSelectedPlatform },
  ])
  const [locations, setLocations] = useState<{ [key: number]: string[] }>({})

  const addLocationPartners = useCallback(debounce(async (locationIds: string[]) => {
    const missingLocations = locationIds.filter((loc) => !Object.keys(locations).includes(loc)) ;
    if (!missingLocations.length) {
      return
    }
    const newPlatforms = await fetchPlatforms(missingLocations)

    setLocations((prevLocations) => {
      const newLocations = { ...prevLocations }
      locationIds.forEach((locationId) => {
        newLocations[Number(locationId)] = newPlatforms.map((platform) => platform.name)
      });

      return newLocations
    })
  }, 750), [locations]);

  const removeLocationPartners = (locationIds: string[]) => {
    setLocations((prevLocations) => {
      const newPartners = { ...prevLocations }
      locationIds.forEach((locationId) => {
        delete newPartners[Number(locationId)]
      })
      return newPartners
    })
  }

  useEffect(() => {
    const updatedPlatforms = new Set([defaultSelectedPlatform, ...Object.values(locations).flat()])
    updatedPlatforms.delete('Chowly')
    const newPlatforms = Array.from(updatedPlatforms).map((platform) => ({
      id: platform.toLowerCase().replace(/\s+/g, '-'),
      name: platform,
    }))
    setPlatforms(newPlatforms)
  }, [locations])

  const fetchPlatforms = async (locationIds: string[] ) => {
    setLoading(true)
    setError(null)
    let platformData: { id: string; name: string }[] = []
    try {
      const response = await getOrderPlatforms(locationIds)
      platformData = response?.data.map((platform) => ({
        id: platform.attributes.name.replace(/\s+/g, '-'),
        name: platform.attributes.name,
      }))
      return platformData || []
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
    return platformData
  }

  return {
    loading,
    error,
    data: platforms,
    locationPartnerIds: Object.keys(locations),
    addLocationPartners,
    removeLocationPartners,
  }
}

export default useLocationOrderPlatforms
